import { SearchableSelect, IconTextItem } from '@/shared/components';
import type { AssetSelectOption, SectionType } from '../model';
import { InternalTransferInfoBanner } from './InternalTransferExchangeInfoBanner';
import type { AccountType } from '@/entities/account';
import { formatReceivedAmount } from '@/shared/lib';

interface InternalTransferAccountCardSelectProps {
  isAssetSelector: boolean;
  assetSelectOptions: AssetSelectOption[] | null;
  accountWithAssetOptions: AssetSelectOption[];
  placeholder: string;
  selectedValue: string;
  handleValueChange: (value: string) => void;
  baseAsset: string;
  sectionType: SectionType;
  assetFromCode?: string;
  isInvalid?: boolean;
  accountType?: AccountType;
}

function InternalTransferAccountCardSelect({
  isAssetSelector,
  assetSelectOptions,
  accountWithAssetOptions,
  accountType,
  placeholder,
  selectedValue,
  sectionType,
  handleValueChange,
  baseAsset,
  isInvalid,
}: InternalTransferAccountCardSelectProps) {
  const options =
    isAssetSelector && assetSelectOptions
      ? assetSelectOptions
      : (accountWithAssetOptions.map(opt => ({
          ...opt,
          quantity: 'equity' in opt ? (opt.equity ?? 0) : opt.available,
          baseQuantity:
            'baseAvailable' in opt
              ? (opt.baseAvailable ?? 0)
              : (opt.baseEquity ?? 0),
        })) as AssetSelectOption[]);

  return (
    <SearchableSelect<AssetSelectOption>
      isInvalid={isInvalid}
      className="w-full"
      floatingLabel={placeholder}
      value={selectedValue}
      onValueChange={handleValueChange}
      contentItemClassname={'pl-4 pr-4 data-[disabled]:opacity-100'}
      items={options}
      disabledItemKey={'isExchangeInfo'}
      renderItem={option => {
        if (option.isExchangeInfo)
          return (
            <InternalTransferInfoBanner
              isAssetSelector={isAssetSelector}
              accountType={accountType}
            />
          );

        const displayBaseQuantity = option.isBaseAsset
          ? 0
          : (option.baseQuantity ?? 0);
        const quantityString = formatReceivedAmount(option.quantity ?? 0);
        const baseQuantityString =
          displayBaseQuantity > 0
            ? formatReceivedAmount(displayBaseQuantity)
            : '';

        const accountHeader = option.accName
          ? `${option.accName}${option.tradingProductCode ? ` ${option.tradingProductCode}` : ''}${option.leverage ? ` ${option.leverage.value}x` : ''}`
          : '';

        return (
          <div
            key={`${option.label}_${sectionType}_option`}
            className={
              option.accName
                ? 'flex flex-col items-start gap-[0.2rem] w-full'
                : 'flex items-center justify-between w-full gap-[1.2rem]'
            }>
            {option.accName && (
              <div className="w-full h-[2rem] font-semibold text-[1.4rem] leading-[2rem] text-primary">
                {accountHeader}
              </div>
            )}
            <div className="flex items-center justify-between w-full gap-[1.2rem]">
              {option.icon && (
                <IconTextItem
                  icon={option.icon}
                  title={option.code || ''}
                  name={option.name || ''}
                />
              )}
              <div className="flex flex-col items-end flex-shrink-0">
                <div className="headline4">{quantityString}</div>
                {baseQuantityString && (
                  <div className="p5 text-secondary">
                    {baseQuantityString} {baseAsset}
                  </div>
                )}
              </div>
            </div>
          </div>
        );
      }}
      renderSelectedValue={opt => {
        const [accNum, assetCode] = opt.split('_');
        const option = options.find(option =>
          isAssetSelector
            ? option.value === opt
            : option.accNum === accNum && option.code === assetCode
        );
        if (!option) return;

        if (option.icon && !option.accNum) {
          return (
            <IconTextItem
              variant="compact"
              icon={option.icon}
              title={option.code || ''}
              name={option.name || ''}
            />
          );
        }
        if (option.accName) {
          return (
            <div className="flex items-center gap-[1.2rem]">
              {option.icon ? (
                <IconTextItem
                  variant="compact"
                  icon={option.icon}
                  title={option.code as string}
                  name={`${option.accName}${option.tradingProductCode ? ` ${option.tradingProductCode}` : ''}${option.leverage?.value ? ` ${option.leverage?.value}x` : ''}`}
                />
              ) : (
                <span className="headline4 text-foreground">
                  {option.code} {option.accName}
                </span>
              )}
            </div>
          );
        }
        return (
          <span className="headline4">{option.label || option.value}</span>
        );
      }}
    />
  );
}

export default InternalTransferAccountCardSelect;
